/**
 * Which screen a path means.
 *
 * A pure function rather than a chain of `if`s inside the component, and a
 * regex table rather than a router library: the participant bundle has a hard
 * size budget (§22), and this is seven routes. Keeping it separate is what
 * lets the admin surface grow routes with a test rather than with a reload.
 */
export type Route =
  | { kind: 'participant'; slug: string; locationCode: string | null }
  | { kind: 'home' }
  | { kind: 'admin_home' }
  | { kind: 'event_editor'; eventId: string }
  | { kind: 'event_report'; eventId: string }
  | { kind: 'redirect'; to: string }
  | { kind: 'not_found' };

/**
 * `/e/:slug` and `/e/:slug/:locationCode`.
 *
 * The second segment is how a QR printed for one venue names that venue, so
 * the participant never sees the picker. It is optional and it is the last
 * one: a third segment is a mistyped URL, not a deeper location.
 */
const PARTICIPANT = /^\/e\/([^/?#]+)(?:\/([^/?#]+))?\/?$/;
const HOME = /^\/$/;
const ADMIN_HOME = /^\/admin\/?$/;
/** Phase 1's event list. The home screen replaced it; the link outlives it. */
const ADMIN_EVENTS = /^\/admin\/events\/?$/;
const EVENT_REPORT = /^\/admin\/events\/([^/]+)\/report\/?$/;
const EVENT_EDITOR = /^\/admin\/events\/([^/]+)\/?$/;

export function resolveRoute(path: string): Route {
  const participant = PARTICIPANT.exec(path);
  if (participant?.[1]) {
    const code = participant[2];
    return {
      kind: 'participant',
      slug: decodeURIComponent(participant[1]),
      locationCode: code ? decodeURIComponent(code) : null,
    };
  }

  if (HOME.test(path)) return { kind: 'home' };
  if (ADMIN_HOME.test(path)) return { kind: 'admin_home' };
  if (ADMIN_EVENTS.test(path)) return { kind: 'redirect', to: '/admin' };

  // Before the editor pattern: `/admin/events/x/report` must not be read as an
  // event whose id happens to contain a slash.
  const report = EVENT_REPORT.exec(path);
  if (report?.[1]) return { kind: 'event_report', eventId: decodeURIComponent(report[1]) };

  const editor = EVENT_EDITOR.exec(path);
  if (editor?.[1]) return { kind: 'event_editor', eventId: decodeURIComponent(editor[1]) };

  return { kind: 'not_found' };
}
